import type { Periodicity } from 'entities/periodicity'
import type { GetTopBrandsQuery } from 'generated/graphql'

import type { BrandRanking, RankMetrics } from './types'

export const checkActiveBrands = (brands: BrandRanking[], activeBrandId: string) => {
  return brands.map(brand => ({
    ...brand,
    active: !activeBrandId ? brand.own : brand.id === activeBrandId,
  }))
}

export const getBrandMetricsByPeriodicity = (brand: BrandRanking, periodicity: Periodicity) => {
  if (!brand) return []
  switch (periodicity) {
    case 'daily':
      return brand.dailyMetrics
    case 'monthly':
      return brand.monthlyMetrics
    default:
      return brand.monthlyMetrics
  }
}

function fillBrandRankingDataMetrics(brands: BrandRanking[]): BrandRanking[] {
  const periodicities: Periodicity[] = ['daily', 'weekly', 'monthly']

  const datesByPeriodicity = periodicities.reduce(
    (acc, periodicity) => {
      const allDates = new Set<string>()

      brands.forEach(brand => {
        const metrics = getBrandMetricsByPeriodicity(brand, periodicity)
        metrics.forEach(metric => {
          if (metric.date) allDates.add(metric.date)
        })
      })

      acc[periodicity] = Array.from(allDates).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      return acc
    },
    {} as Record<Periodicity, string[]>
  )

  const createEmptyMetric = (date: string): RankMetrics => ({
    date,
    score: null,
    variation: null,
    rank: null,
    rankVariation: null,
  })

  return brands.map(brand => {
    const updatedBrand = { ...brand }

    const dailyMetrics = getBrandMetricsByPeriodicity(brand, 'daily')
    const monthlyMetrics = getBrandMetricsByPeriodicity(brand, 'monthly')

    const sortedDailyDates = datesByPeriodicity['daily']
    const sortedMonthlyDates = datesByPeriodicity['monthly']

    const dailyMetricsMap = new Map(dailyMetrics.map(metric => [metric.date, metric]))
    const monthlyMetricsMap = new Map(monthlyMetrics.map(metric => [metric.date, metric]))

    const normalizedDailyMetrics = sortedDailyDates.map(date => dailyMetricsMap.get(date) || createEmptyMetric(date))
    const normalizedMonthlyMetrics = sortedMonthlyDates.map(
      date => monthlyMetricsMap.get(date) || createEmptyMetric(date)
    )

    updatedBrand.dailyMetrics = normalizedDailyMetrics
    updatedBrand.monthlyMetrics = normalizedMonthlyMetrics

    return updatedBrand
  })
}

function transformResponseMetricToData(responseMetric: any): RankMetrics {
  return {
    date: responseMetric?.date,
    score: Number(responseMetric?.value) * 100,
    variation: Number(responseMetric?.valueVariation) * 100,
    rank: responseMetric?.rank,
    rankVariation: responseMetric?.rankVariation,
  }
}

export function transformResponseToBrandRankingData(data: GetTopBrandsQuery, selfBrandId: string): BrandRanking[] {
  const topBrands = Array.from(data?.getWorkspace?.topBrandSOAByWorkspace?.topBrandSOAPaginated?.data ?? [])
  const mappedBrands = topBrands.map(brand => {
    const dailyMetrics = Array.from(brand?.dailySOAMetric ?? []).map(transformResponseMetricToData)
    const monthlyMetrics = Array.from(brand?.monthlySOAMetric ?? []).map(transformResponseMetricToData)

    const sortedDailyMetrics = dailyMetrics.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    const sortedMonthlyMetrics = monthlyMetrics.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    const lastSevenDaysMetrics = sortedDailyMetrics.slice(0, 7)

    const last3MonthsMetric = transformResponseMetricToData(brand?.last3Month)
    const last6MonthsMetric = transformResponseMetricToData(brand?.last6Month)
    const last12MonthsMetric = transformResponseMetricToData(brand?.last12Month)

    const currentMetrics =
      [last3MonthsMetric, sortedMonthlyMetrics[0], sortedDailyMetrics[0]].find(metric => metric?.score) ||
      last3MonthsMetric

    const category = brand?.category

    return {
      id: brand?.brand?.id,
      name: brand?.brand?.name,
      thumbnail: brand.thumbnail,
      rank: currentMetrics.rank,
      score: currentMetrics.score,
      variation: currentMetrics.variation,
      rankVariation: currentMetrics.rankVariation,
      category: category ? { id: category.id, name: category.name } : null,
      color: brand?.brand?.brandColor,
      own: brand?.brand?.id === selfBrandId,
      dailyMetrics: sortedDailyMetrics,
      monthlyMetrics: sortedMonthlyMetrics,
      lastMetrics: lastSevenDaysMetrics,
      last3MonthsMetric,
      last6MonthsMetric,
      last12MonthsMetric,
    }
  })

  return fillBrandRankingDataMetrics(mappedBrands)
}
